import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    addTeamMemberAPI,
    changeTeamLeaderAPI,
    createTeamAPI,
    deleteTeamAPI,
    getCenterCoachesAPI,
    getCenterSalesManagersAPI,
    getSuperAdminTeamBreakdownAPI,
    getSuperAdminTeamsAPI,
    getTeamAPI,
    getTeamFeatureStatusAPI,
    listTeamsAPI,
    rebackfillTeamAPI,
    removeTeamMemberAPI,
    updateTeamAPI,
} from './api';
import { teamKeys } from './keys';
import { AddTeamMemberBody, CreateTeamBody, TeamMemberInput } from './types';

// === 팀 기능 노출 여부 ===

export const useTeamFeatureStatus = ({
    centerId,
    enabled = true,
}: {
    centerId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: ['teamFeatureStatus', centerId],
        queryFn: () => getTeamFeatureStatusAPI(centerId),
        enabled: enabled && !!centerId,
        staleTime: 5 * 60 * 1000, // 5분 — 자주 안 바뀜
        select: (res) => res.data,
    });

// === 센터 강사 옵션 ===

export const useCenterCoaches = ({
    centerId,
    enabled = true,
}: {
    centerId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: ['centerCoaches', centerId],
        queryFn: () => getCenterCoachesAPI(centerId),
        enabled: enabled && !!centerId,
        select: (res) => res.data?.registeredTrainer ?? [],
    });

// === 센터 판매담당자 (강사 + 영업) — 표시·옵션 통합 hook ===
//
// 반환:
//   - all: 퇴사자 포함 전체 (이름 표시 매핑용 — 팀에 남아있던 퇴사 강사 이름 누락 방지)
//   - active: 퇴사자 제외 (신규 팀원 선택용)
//
// 백엔드 salesManagers API는 퇴사 강사도 isDeleted:true로 같이 반환하므로,
// 옵션과 매핑을 같은 페이로드에서 분리한다.

export const useCenterSalesManagers = ({
    centerId,
    enabled = true,
}: {
    centerId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: ['centerSalesManagers', centerId],
        queryFn: () => getCenterSalesManagersAPI(centerId),
        enabled: enabled && !!centerId,
        select: (res) => {
            const all = res.data?.salesManagers ?? [];
            return {
                all,
                active: all.filter((m) => !m.isDeleted),
            };
        },
    });

// === 센터관리자 ===

export const useTeams = ({
    centerId,
    status = 'active',
    enabled = true,
}: {
    centerId: string;
    status?: 'active' | 'all';
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: teamKeys.list(centerId, status),
        queryFn: () => listTeamsAPI(centerId, status),
        enabled: enabled && !!centerId,
        select: (res) => res.data,
    });

export const useTeam = ({
    centerId,
    teamId,
    enabled = true,
}: {
    centerId: string;
    teamId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: teamKeys.detail(centerId, teamId),
        queryFn: () => getTeamAPI(centerId, teamId),
        enabled: enabled && !!centerId && !!teamId,
        select: (res) => res.data,
    });

export const useCreateTeam = (centerId: string) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateTeamBody) => createTeamAPI(centerId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: teamKeys.all });
        },
    });
};

export const useUpdateTeam = (centerId: string, teamId: string) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: { name?: string; description?: string }) =>
            updateTeamAPI(centerId, teamId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: teamKeys.all });
        },
    });
};

export const useDeleteTeam = (centerId: string) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (teamId: string) => deleteTeamAPI(centerId, teamId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: teamKeys.all });
        },
    });
};

export const useAddTeamMember = (centerId: string, teamId: string) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: AddTeamMemberBody) => addTeamMemberAPI(centerId, teamId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: teamKeys.all });
        },
    });
};

export const useRemoveTeamMember = (centerId: string, teamId: string) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (userId: string) => removeTeamMemberAPI(centerId, teamId, userId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: teamKeys.all });
        },
    });
};

export const useRebackfillTeam = (centerId: string, teamId: string) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: { effectiveFrom?: string | null }) =>
            rebackfillTeamAPI(centerId, teamId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: teamKeys.all });
        },
    });
};

export const useChangeTeamLeader = (centerId: string, teamId: string) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: TeamMemberInput) => changeTeamLeaderAPI(centerId, teamId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: teamKeys.all });
        },
    });
};

// === 슈퍼관리자 분석 ===

export const useSuperAdminTeams = ({
    superAdminId,
    from,
    to,
    centerIds,
    includeDeleted,
    includeGroup,
    enabled = true,
}: {
    superAdminId: string;
    from: string;
    to: string;
    centerIds?: string[];
    includeDeleted?: boolean;
    includeGroup?: boolean;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [
            ...teamKeys.superAdminAnalytics(superAdminId, from, to, centerIds),
            includeDeleted ?? false,
            includeGroup ?? false,
        ],
        queryFn: () =>
            getSuperAdminTeamsAPI(superAdminId, {
                from,
                to,
                centerIds,
                includeDeleted,
                includeGroup,
            }),
        enabled: enabled && !!superAdminId && !!from && !!to,
        select: (res) => res.data,
    });

export const useSuperAdminTeamBreakdown = ({
    superAdminId,
    teamId,
    from,
    to,
    includeGroup,
    enabled = true,
}: {
    superAdminId: string;
    teamId: string;
    from: string;
    to: string;
    includeGroup?: boolean;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [
            ...teamKeys.superAdminBreakdown(superAdminId, teamId, from, to),
            includeGroup ?? false,
        ],
        queryFn: () =>
            getSuperAdminTeamBreakdownAPI(superAdminId, teamId, { from, to, includeGroup }),
        enabled: enabled && !!superAdminId && !!teamId,
        select: (res) => res.data,
    });
